/**
 * Onde a linha de cada pessoa começa e termina, no Comparativo do time.
 *
 * <p>O eixo mostra o mês inteiro, mas a série vai <b>do dia 1 até hoje</b> — e nem um dia
 * além. As duas pontas têm motivos diferentes, e é por isso que a regra mora aqui em vez
 * de num `slice` solto no meio do componente.
 *
 * <p><b>O futuro fica de fora.</b> Desenhar até o dia 31 punha zeros no que ainda não
 * aconteceu, e o resultado era um despencar logo depois de hoje: parecia que o time tinha
 * parado de trabalhar, quando o que havia ali era mês que ainda não chegou.
 *
 * <p><b>O passado aparece, mesmo vazio.</b> Antes a linha parava no último dia lançado, e
 * quem não tinha lançado nada não tinha linha nenhuma. Isso escondia justamente o que a
 * tela existe para mostrar: dia sem lançamento <b>é</b> informação, e some quando a linha
 * simplesmente acaba. Agora ela desce até o zero e segue — dá para ver de relance quem
 * está em dia e quem não está, que é a pergunta que se faz olhando este gráfico.
 */

/**
 * O índice de hoje na régua de dias do mês.
 *
 * <p>Num mês passado nenhum dia é hoje, e aí o limite é o último dia do mês — o mês
 * inteiro já aconteceu, e não há futuro a esconder. Num mês futuro também não há hoje, e
 * o resultado é o mesmo; é aceitável porque as células vêm todas em zero de qualquer
 * forma, e navegar para um mês que não começou é caso de curiosidade, não de trabalho.
 */
export function limiteDeHoje(dias: { hoje: boolean }[]): number {
  const hoje = dias.findIndex((d) => d.hoje);
  return hoje >= 0 ? hoje : dias.length - 1;
}

/**
 * A série de uma pessoa: as horas do dia 1 até `limite`, zeros inclusive.
 *
 * <p>Devolve vazio só quando não há dia nenhum para mostrar — mês sem dias, que não
 * acontece na prática, mas o componente ainda checa antes de desenhar.
 */
export function serieAteHoje(horas: number[], limite: number): number[] {
  if (limite < 0) return [];
  return horas.slice(0, limite + 1);
}

/**
 * O desvio vertical de quem empatou no mesmo dia e no mesmo valor.
 *
 * <p>Duas pessoas com 3h no dia 2 desenham uma em cima da outra, e a que for pintada
 * depois engole a primeira. O desvio afasta as duas o suficiente para que se leiam.
 *
 * <p><b>No zero ele é só para cima</b>, e isso passou a importar quando as linhas
 * deixaram de parar no último lançamento: agora todo mundo que não lançou num dia empata
 * em zero, e o zero é o chão do gráfico. Com o desvio simétrico, metade do grupo caía
 * fora da área e era grampeada no mesmo pixel da borda — três linhas viravam uma, que é
 * exatamente o que o desvio existe para impedir. Empurrando o grupo todo para cima, a
 * ordem se mantém e ninguém é grampeado.
 *
 * <p>O <b>passo</b> é escolha de quem chama, e no zero ele é menor: lá não há bolinha
 * para caber, e é onde mais gente empata — um leque largo acima do eixo faz o olho
 * duvidar se aquilo é zero mesmo.
 *
 * @param posicao índice da pessoa na lista de empatados, já ordenada de cima para baixo
 * @param quantos quantas pessoas empataram naquele ponto
 * @param noChao   se o valor empatado é o zero, que é a borda de baixo do gráfico
 * @param passo    a distância entre duas linhas empatadas, em unidades do viewBox
 */
export function desvioDoEmpate(
  posicao: number,
  quantos: number,
  noChao: boolean,
  passo: number
): number {
  if (quantos < 2) return 0;
  // y cresce para baixo no SVG: negativo é para cima. O `|| 0` troca o `-0` que a
  // multiplicação por zero produz — ele desenha igual, mas suja quem comparar o valor.
  if (noChao) return -(quantos - 1 - posicao) * passo || 0;
  return (posicao - (quantos - 1) / 2) * passo;
}
